import type * as CommerceSdk from 'commerce-sdk-isomorphic'

type TokenResponse = Awaited<ReturnType<typeof CommerceSdk.helpers.refreshAccessToken>>

export function useRefreshTokens() {
  const error = ref<null | Error>(null)
  // A. Dedupe concurrent client-side refreshes so parallel getAccessToken() calls make one SLAS call.
  const refreshing = useState<Promise<boolean> | null>('_refresh_in_progress', () => null)

  async function didRefresh() {
    const authGeneration = useState<number>('_auth_generation', () => 0)
    const gen = authGeneration.value

    const userStore = useUserStore()
    const wasLoggedIn = Boolean(userStore.isLoggedIn)
    const refreshToken = useCookie('refresh_token').value
    let shouldClearAuthCookies = false

    if (refreshToken) {
      let response: TokenResponse | null = null
      let refreshError: Error | null = null
      try {
        const result = await useShopper().helpers.refreshAccessToken(useShopper().shopperLogin, { refreshToken })
        if (!isValidTokenResponse(result)) throw new Error('Malformed refresh token response')
        response = result
      } catch (e) {
        refreshError = normalizeError(e)
      }

      // Must run before any cookie write / state mutation, on every post-await path (success,
      // malformed shape and rejection alike) — see useRefreshTokens.logout.nuxt.test.ts.
      if (authGeneration.value !== gen) return false

      if (response) {
        setAuthCookies({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          expiresIn: response.expires_in,
          refreshTokenExpiresIn: response.refresh_token_expires_in,
        })
        return true
      }

      // Consumed or expired refresh token (single-use policy), or a malformed response — fall through
      // to guest-login recovery below.
      console.warn('Token refresh failed, falling back to guest login:', refreshError?.message)
      error.value = refreshError
      shouldClearAuthCookies = true
    }

    if (wasLoggedIn) useBrazeTracking().syncLoggedInStatus(false)
    if (shouldClearAuthCookies) clearCookies(['access_token', 'refresh_token'])

    // Clear user state so the UI reflects the change before the guest token lands.
    userStore.setUser(null)

    // A guest token lets an in-page action (e.g. add to bag) recover without requiring a navigation.
    const guestOk = await useGuestLogin().getAndSetGuestTokens()
    if (authGeneration.value !== gen) return false

    if (!guestOk) {
      error.value = new Error('Failed to obtain guest token after refresh failure')
    }
    return guestOk
  }

  async function getAccessToken() {
    const accessToken = useCookie('access_token').value

    if (!isTokenValid()) {
      // B. Server middleware owns SSR tokens — don't race it. With A, this kills the SSR refresh herd.
      if (import.meta.server) return null

      if (!refreshing.value) {
        refreshing.value = didRefresh().finally(() => {
          refreshing.value = null
        })
      }
      const ok = await refreshing.value
      return ok ? useCookie('access_token').value : null
    }

    return accessToken
  }

  return { refresh: didRefresh, error, getAccessToken }
}
